/**
 * InPageTabs — a NET-NEW in-page (sub-page) tab strip for the admin surface
 * (ADR-074 §3.5). This is the in-page tab pattern (the top-level nav is the COG
 * menu in the AppHeader; the legacy AdminTabStrip was removed in BOW-finesse B6).
 * `admin-portal-ui-standards.md` has no in-page tab pattern, so this is the
 * canonical one — use it (do not invent a fresh shape per page).
 *
 * Controlled: the parent owns the active key (so a tab can be deep-linked via the
 * URL — e.g. the Email alert "View →" lands on the Email Validation tab).
 */
export interface InPageTab<K extends string> {
  key: K;
  label: string;
  /** Optional count badge (e.g. issue count). */
  badge?: number;
  /**
   * A planned-but-unbuilt section: rendered so the roadmap is visible, but inert.
   *
   * Added for the Growth surface (ADR-114), which ships a tab strip with one real tab and
   * three roadmap tabs. EXTENDING the shared component rather than forking a near-copy is
   * the component library's reuse-first rule — and the alternative here was worse than
   * duplication: a tab that looks clickable, is not, and gives no reason why.
   *
   * Optional and defaulted, so every existing caller is unaffected.
   */
  disabled?: boolean;
}

export interface InPageTabsProps<K extends string> {
  tabs: InPageTab<K>[];
  active: K;
  onChange: (key: K) => void;
  ariaLabel?: string;
}

export function InPageTabs<K extends string>({
  tabs,
  active,
  onChange,
  ariaLabel = 'Sections',
}: InPageTabsProps<K>) {
  return (
    <div className="portal-inpage-tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={t.key === active}
          // Both the native attribute AND aria-disabled: the first stops the click and
          // removes it from the tab order, the second is what a screen reader announces.
          disabled={t.disabled}
          aria-disabled={t.disabled || undefined}
          title={t.disabled ? 'Planned — not built yet' : undefined}
          className={
            `portal-inpage-tab${t.key === active ? ' portal-inpage-tab-active' : ''}`
            + `${t.disabled ? ' portal-inpage-tab-disabled' : ''}`
          }
          onClick={() => { if (!t.disabled) onChange(t.key); }}
        >
          {t.label}
          {typeof t.badge === 'number' && t.badge > 0 && (
            <span className="portal-inpage-tab-badge">{t.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}
